import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { IWaitForClick } from "../../../models/interactions/WaitForClick";
import Interaction from "../../../models/interactions/Interaction";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import NodeAllPropsDropdown from "../components/NodeAllPropsDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import usePropHelper from "../../../hooks/PropHelper";
import { selectUsedProps } from "../../../app/slices/PropLibrarySlice";
import PiInputfield from "../../../components/PiInputField";
import NodeToggle from "../components/NodeToggle";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { motion, AnimatePresence } from "framer-motion";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import { applyPickedPropToInteraction } from "./SequencePropPicker.helpers";

export default function WaitForClickNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const interaction = props.interaction as IWaitForClick;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const { getUsedPropByInstanceId } = usePropHelper();
  const usedProps = useSelector(selectUsedProps);
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const [expanded, setExpanded] = useState(false);

  const usedProp = useMemo(() => {
    if (!interaction.propGuid) return null;
    return getUsedPropByInstanceId(interaction.propGuid);
  }, [interaction.propGuid, usedProps]);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "prop";

  const propDropdown = useMemo(
    () => (
      <NodeAllPropsDropdown
        label="Prop"
        selectedPropStateId={interaction.propGuid}
        accessory={
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-prop-on-map-button"
            className={
              "group flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px] transition-all duration-150 " +
              (isPickerActiveForNode
                ? "border-creator-primary bg-creator-primary text-white shadow-[0_0_0_4px_rgba(58,196,255,0.18)]"
                : "border-creator-primary bg-creator-primary-background text-creator-primary hover:bg-creator-primary-washed")
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "prop",
                  title: "Pick prop",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        }
        onClick={(propGuid) => {
          const prop = { id: propGuid } as any;
          updateInteraction(props.nodeId, {
            ...applyPickedPropToInteraction(
              interaction,
              prop,
              getUsedPropByInstanceId,
            ),
          } as IWaitForClick);
        }}
      />
    ),
    [
      dispatch,
      getUsedPropByInstanceId,
      interaction,
      isPickerActiveForNode,
      props.nodeId,
      updateInteraction,
    ],
  );

  const tagDropdown = useMemo(
    () => (
      <NodeStringDropdown
        items={usedProp?.metadata.clickableTags.map((p) => p.name) ?? []}
        selectedItem={interaction.tag}
        label="Tag"
        onClick={(tag) => {
          updateInteraction(props.nodeId, {
            ...interaction,
            tag: tag,
          } as IWaitForClick);
        }}
      />
    ),
    [usedProp, interaction.tag, updateInteraction, props.nodeId],
  );

  const indicateToggle = useMemo(
    () => (
      <NodeToggle
        label="Indicate"
        value={interaction.indicate}
        onChange={(indicate) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            indicate,
          } as IWaitForClick)
        }
      />
    ),
    [interaction.indicate, updateInteraction, props.nodeId],
  );

  const indicateDelayInputfield = useMemo(
    () => (
      <PiInputfield
        label="Indicate Delay"
        placeholder="Indicate Delay"
        value={interaction.indicateDelay.toString()}
        onChange={(indicateDelay: string) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            indicateDelay: Number(indicateDelay),
          } as IWaitForClick)
        }
        disabled={!interaction.indicate}
      />
    ),
    [
      interaction.indicateDelay,
      interaction.indicate,
      updateInteraction,
      props.nodeId,
    ],
  );

  const expandButton = useMemo(
    () => (
      <div
        className="flex flex-row gap-2 w-full h-full text-[24px] items-center justify-center text-creator-primary cursor-pointer hover:text-creator-primary-hover"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <KeyboardDoubleArrowUpIcon fontSize="inherit" color="inherit" />
        ) : (
          <KeyboardDoubleArrowDownIcon fontSize="inherit" color="inherit" />
        )}
      </div>
    ),
    [expanded],
  );

  const expandedContent = (
    <motion.div
      className="flex flex-col gap-[12px] overflow-hidden h-full"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "112px", transition: { duration: 0.1 } }}
      exit={{ opacity: 0, height: 0, transition: { duration: 0.1 } }}
    >
      {indicateToggle}
      {indicateDelayInputfield}
    </motion.div>
  );

  return (
    <div className="flex flex-col gap-[12px] w-full h-full">
      {propDropdown}
      {tagDropdown}
      <AnimatePresence initial={false}>
        {expanded && expandedContent}
      </AnimatePresence>
      {expandButton}
    </div>
  );
}
